import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { checkAuthStatus } from '../services/authService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  const checkAuth = useCallback(async () => {
    try {
      const authStatus = await checkAuthStatus();
      setAuthState({
        isAuthenticated: authStatus.isAuthenticated,
        user: authStatus.user || null,
        isLoading: false,
      });
      return authStatus;
    } catch (error) {
      console.error('Auth check failed:', error);
      // If token is invalid, clear it from storage
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
      }
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
      }));
      return { isAuthenticated: false };
    }
  }, []);

  useEffect(() => {
    // Initial auth check
    checkAuth();

    // Set up interval for periodic checks (every 30 seconds)
    const intervalId = setInterval(checkAuth, 30000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [checkAuth]);

  const login = async (token) => {
    localStorage.setItem('token', token);
    return checkAuth();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
    });
    // Redirect to home after logout
    window.location.href = '/home';
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
